import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import { lineClusterKeyFromContext, type LineClusterAxes } from './lineClusterKey'
import type { LineSignalContextInput } from './lineLearningSignals'
import type { LineWorkspacePolicy } from './lineWorkspacePolicy'

const CACHE_TTL_MS = 5 * 60_000
const MAX_KNOB_CHARS = 2_000
const suspiciousText = /(?:ignore\s+(?:all\s+)?(?:previous|system|developer)|system\s+prompt|developer\s+message|<script|```|https?:\/\/)/i
const fields = ['encouragements', 'cautions', 'patterns', 'antiPatterns'] as const

export type LiveLineKnobs = Record<(typeof fields)[number], string[]> & { version?: number; warmupComplete: true }
export type LiveLineKnobContext = LineSignalContextInput
export type ResolvedLiveLineKnobs = { clusterId: string; axes: LineClusterAxes; knobs: LiveLineKnobs; preamble: string }

const cache = new Map<string, { expiresAt: number; value: ResolvedLiveLineKnobs | null }>()
const configCache = new Map<string, { expiresAt: number; value: { liveKnobsEnabled: boolean; globalKillSwitch: boolean } }>()
const strings = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

export const normalizeLiveLineKnobs = (value: unknown): LiveLineKnobs | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const source = value as Record<string, unknown>
  if (source.warmupComplete !== true) return null
  const normalized = Object.fromEntries(fields.map((field) => [field, strings(source[field])])) as Record<(typeof fields)[number], string[]>
  const all = fields.flatMap((field) => normalized[field])
  if (all.length > 8 || all.some((item) => !item.trim() || item.length > 240 || suspiciousText.test(item))) return null
  const result: LiveLineKnobs = { ...normalized, warmupComplete: true }
  if (Number.isSafeInteger(source.version)) result.version = Number(source.version)
  return JSON.stringify(result).length <= MAX_KNOB_CHARS ? result : null
}

export const formatLiveLineKnobPreamble = (axes: LineClusterAxes, knobs: LiveLineKnobs) => [
  `## PRIOR OUTCOMES (context: ${axes.planType}/${axes.workspaceType}/${axes.attachmentDensity}/${axes.timelineSpan})`,
  'Approaches that worked well:', ...knobs.encouragements.map((item) => `- ${item}`),
  'Patterns to prefer:', ...knobs.patterns.map((item) => `- ${item}`),
  'Approaches to avoid:', ...knobs.cautions.map((item) => `- ${item}`),
  'Anti-patterns:', ...knobs.antiPatterns.map((item) => `- ${item}`),
].join('\n').slice(0, MAX_KNOB_CHARS)

const reportSanityFailure = (workspaceId: string, clusterId: string) => {
  void httpsCallable<{ workspaceId: string; clusterId: string }, unknown>(firebaseFunctions, 'reportLineKnobSanityFailure')({ workspaceId, clusterId }).catch(() => undefined)
}

const getLiveKnobConfig = async (workspaceId: string, policy: LineWorkspacePolicy) => {
  if (policy.learning.globalKillSwitch) return { liveKnobsEnabled: false, globalKillSwitch: true }
  const cached = configCache.get(workspaceId)
  if (cached && cached.expiresAt > Date.now()) return cached.value
  try {
    const response = await httpsCallable<{ workspaceId: string }, { liveKnobsEnabled: boolean; globalKillSwitch: boolean }>(firebaseFunctions, 'getLineLiveKnobConfig')({ workspaceId })
    const value = { liveKnobsEnabled: response.data.liveKnobsEnabled === true, globalKillSwitch: response.data.globalKillSwitch === true }
    configCache.set(workspaceId, { expiresAt: Date.now() + CACHE_TTL_MS, value })
    return value
  } catch {
    return { liveKnobsEnabled: false, globalKillSwitch: true }
  }
}

export const getLiveLineKnobs = async ({ workspaceId, context, policy }: { workspaceId: string; context: LiveLineKnobContext; policy: LineWorkspacePolicy }): Promise<ResolvedLiveLineKnobs | null> => {
  if (!workspaceId) return null
  const config = await getLiveKnobConfig(workspaceId, policy)
  if (!config.liveKnobsEnabled || config.globalKillSwitch) return null
  const { clusterId, axes } = lineClusterKeyFromContext(context)
  const cached = cache.get(clusterId)
  if (cached && cached.expiresAt > Date.now()) return cached.value
  try {
    const snapshot = await getDoc(doc(firestoreDb, 'line_knobs_live', clusterId))
    if (!snapshot.exists()) {
      cache.set(clusterId, { expiresAt: Date.now() + CACHE_TTL_MS, value: null })
      return null
    }
    const knobs = normalizeLiveLineKnobs(snapshot.data())
    if (!knobs) {
      reportSanityFailure(workspaceId, clusterId)
      cache.set(clusterId, { expiresAt: Date.now() + CACHE_TTL_MS, value: null })
      return null
    }
    const value = { clusterId, axes, knobs, preamble: formatLiveLineKnobPreamble(axes, knobs) }
    cache.set(clusterId, { expiresAt: Date.now() + CACHE_TTL_MS, value })
    return value
  } catch {
    return null
  }
}

export const clearLiveLineKnobsCache = () => { cache.clear(); configCache.clear() }
